import { assign, createMachine } from 'xstate';
import {
  addMilliseconds,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';

/** Earliest/latest instants supported by the JS Date constructor. */
const MIN_JS_DATE = new Date(-8640000000000000);
const MAX_JS_DATE = new Date(8640000000000000);

/** Absolute min/max from millisecond offsets (e.g. max -1 → reference − 1 ms). */
export const getDateBoundsFromOffsets = (
  minOffset: number,
  maxOffset: number,
  referenceDate: Date,
) => ({
  minDate: Number.isFinite(minOffset)
    ? addMilliseconds(referenceDate, minOffset)
    : MIN_JS_DATE,
  maxDate: Number.isFinite(maxOffset)
    ? addMilliseconds(referenceDate, maxOffset)
    : MAX_JS_DATE,
});

/** Tightest allowed window: latest min, earliest max. */
export const combineDateBounds = (
  durationMin: Date,
  durationMax: Date,
  fieldMin: Date,
  fieldMax: Date,
) => ({
  minDate: isAfter(durationMin, fieldMin) ? durationMin : fieldMin,
  maxDate: isBefore(durationMax, fieldMax) ? durationMax : fieldMax,
});

export const getMinMaxTimeForSelectedDate = (
  selectedDate: Date | null | undefined,
  minDate: Date,
  maxDate: Date,
  minTimeFloor?: Date | null,
) => {
  const anchorDate = selectedDate ?? minTimeFloor;
  if (!anchorDate) {
    return {};
  }

  const calendarDay = selectedDate ?? minDate;

  let minTime = isSameDay(calendarDay, minDate)
    ? minDate
    : startOfDay(calendarDay);

  if (
    minTimeFloor &&
    isSameDay(calendarDay, minTimeFloor) &&
    isAfter(minTimeFloor, minTime)
  ) {
    minTime = minTimeFloor;
  }

  return {
    minTime,
    maxTime: isSameDay(anchorDate, maxDate) ? maxDate : endOfDay(anchorDate),
  };
};

/** End of a range must be strictly after start (defaults to start + step). */
export const ensureEndDateAfterStart = (
  startDate: Date,
  endDate: Date | null | undefined,
  timeStepMs: number,
): Date => {
  if (endDate && isAfter(endDate, startDate)) {
    return endDate;
  }
  return addMilliseconds(startDate, timeStepMs);
};

export const clampDateToMinMax = (
  value: Date | null | undefined,
  minDate: Date,
  maxDate: Date,
): Date | null => {
  if (!value) {
    return null;
  }
  if (isBefore(value, minDate)) {
    return minDate;
  }
  if (isAfter(value, maxDate)) {
    return maxDate;
  }
  return value;
};

export const isCalendarDayWithinBounds = (
  calendarDay: Date,
  minDate: Date,
  maxDate: Date,
): boolean =>
  !isBefore(endOfDay(calendarDay), minDate) &&
  !isAfter(startOfDay(calendarDay), maxDate);

const isMidnight = (value: Date) =>
  value.getHours() === 0 &&
  value.getMinutes() === 0 &&
  value.getSeconds() === 0 &&
  value.getMilliseconds() === 0;

/**
 * Calendar day clicks from react-datepicker arrive at midnight with an event.
 * Time picker commits also use midnight for 12:00 AM but pass a null event.
 */
export const shouldPreserveTimeOnCalendarDayPick = (
  changeDate: Date | null | undefined,
  priorDate: Date | null | undefined,
  event: unknown,
  showTimeInput: boolean,
): boolean =>
  Boolean(
    showTimeInput &&
      changeDate &&
      priorDate &&
      event != null &&
      isMidnight(changeDate),
  );

export const getDateMilliseconds = (dateValue) => {
  if (!dateValue) {
    return undefined;
  }

  const parsedDate =
    dateValue instanceof Date ? dateValue : new Date(dateValue);
  const milliseconds = parsedDate.getTime();

  return Number.isNaN(milliseconds) ? undefined : milliseconds;
};

export const isSameDateValue = (
  firstDate: Date | null | undefined,
  secondDate: Date | null | undefined,
) => getDateMilliseconds(firstDate) === getDateMilliseconds(secondDate);

export const isSameDateRangeValue = (
  firstDateRange: [Date | null, Date | null] = [null, null],
  secondDateRange: [Date | null, Date | null] = [null, null],
) =>
  isSameDateValue(firstDateRange?.[0], secondDateRange?.[0]) &&
  isSameDateValue(firstDateRange?.[1], secondDateRange?.[1]);

export const getDatePickerScenario = (picker) => {
  switch (picker) {
    case 'year':
      return {
        scenario: 'yearPicker',
        config: {
          dateSelectionAllowed: false,
          monthSelectionAllowed: false,
          quarterSelectionAllowed: false,
          yearSelectionAllowed: true,
        },
      };

    case 'quarter':
      return {
        scenario: 'quarterPicker',
        config: {
          dateSelectionAllowed: false,
          monthSelectionAllowed: false,
          quarterSelectionAllowed: true,
          yearSelectionAllowed: true,
        },
      };

    case 'month':
      return {
        scenario: 'monthPicker',
        config: {
          dateSelectionAllowed: false,
          monthSelectionAllowed: true,
          quarterSelectionAllowed: false,
          yearSelectionAllowed: true,
        },
      };

    default:
      return {
        scenario: 'datePicker',
        config: {
          dateSelectionAllowed: true,
          monthSelectionAllowed: true,
          quarterSelectionAllowed: false,
          yearSelectionAllowed: true,
        },
      };
  }
};

// eslint-disable-next-line max-lines-per-function
export const getDateTimePickerMachine = (scenario, config = {}) =>
  createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QQIYBcwBUCWBbMAdKhgArYDGA1mAE4DEAEgKICCAIkwEoD6AsgPIA5TAwDaABgC6iUAAcA9rGxps8gHYyQAD0QAmACwAOArsMBGXQFZxAdnGWzlywBoQATz0BOfQU+6AbFZW-iGO+gC+4a7EWHiEMWRUtIysHDwAmqycEtJIIApKKuqaOgg2uq4eCJae4r4BBjb6Zp42nu2R0eix+AS46mgAFonU9ADCADIAkmMA0jmaBcqqGnmlBsamFtZ2Dk6Vevo++g2WhudmAMz+npcRUSAxOL39akMjyczsXNyZLNlSRaKZbFNZ6MzGez6G7+ZrifxmXYHMqBAiIwKXNr6GwhQyeTqPbrPQgARwArt0aB96F80r8sgs8ksiqtQKVxMjxASnnECG4wCgqRRRnRJjN5oCmcCWSVwZDLNDPLCzPDEfZkTYzGYCP4cQYToETrp8Q8eb1+YLqaLpnNRGZcnJpStZQgOe5EFyCWp5BA4JozWAgYVnWCEABaIzIsOGGwEfTtdo1IxHJzcom8hLC2hBkGs7ReZEtbV+QJOTE2Jz6cT3LoYYl9AbDLM0HMy0O6jUKgg1Uy1Ry6cS6Fppuu88mU6mtkNsj3I86eAjiPyGfz2Tx4-yWXQjnqEC1CpItqXB0EzhDQnzeSyXS6Do7Y84a1cES6nKwnfSXdeRSJAA */
    id: 'dateTime',
    type: 'compound',
    initial: scenario ?? 'datePicker',
    context: config ?? {
      dateSelectionAllowed: true,
      monthSelectionAllowed: true,
      quarterSelectionAllowed: false,
      yearSelectionAllowed: true,
    },
    on: {
      SET_PICKER: [
        {
          guard: ({ event }) => event.scenario === 'datePicker',
          target: '.datePicker',
          actions: assign(({ event }) => event.config ?? {}),
        },
        {
          guard: ({ event }) => event.scenario === 'monthPicker',
          target: '.monthPicker',
          actions: assign(({ event }) => event.config ?? {}),
        },
        {
          guard: ({ event }) => event.scenario === 'quarterPicker',
          target: '.quarterPicker',
          actions: assign(({ event }) => event.config ?? {}),
        },
        {
          guard: ({ event }) => event.scenario === 'yearPicker',
          target: '.yearPicker',
          actions: assign(({ event }) => event.config ?? {}),
        },
      ],
    },
    states: {
      datePicker: {
        on: {
          HEADER_MONTH: [
            {
              guard: ({ context }) => context.monthSelectionAllowed,
              target: 'monthPicker',
            },
          ],
          HEADER_YEAR: [
            {
              guard: ({ context }) => context.yearSelectionAllowed,
              target: 'yearPicker',
            },
          ],
        },
      },
      monthPicker: {
        on: {
          CLICK: [
            {
              guard: ({ context }) => context.dateSelectionAllowed,
              target: 'datePicker',
            },
          ],
          HEADER_YEAR: [
            {
              guard: ({ context }) => context.yearSelectionAllowed,
              target: 'yearPicker',
            },
          ],
        },
      },
      quarterPicker: {
        on: {
          HEADER_YEAR: 'yearPicker',
        },
      },
      yearPicker: {
        on: {
          CLICK: [
            {
              guard: ({ context }) => context.monthSelectionAllowed,
              target: 'monthPicker',
            },
            {
              guard: ({ context }) => context.quarterSelectionAllowed,
              target: 'quarterPicker',
            },
          ],
        },
      },
    },
  });
